/**
 * Feature Access Provider Component
 *
 * Provides feature access checking based on user's current plan
 * Manages which features the user has access to based on their subscription
 */

import React, { useMemo } from "react";
import {
  FeatureAccessContext,
  type FeatureAccessContextType,
} from "./FeatureAccessContext";
import { useAuth } from "../hooks/useAuth";
import { usePlans } from "../hooks/usePlans";
import { useFeatures } from "../hooks/useFeatures";

interface FeatureAccessProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for feature access context
 * Wraps the app to provide feature checking capabilities
 *
 * Note: This provider should be placed inside SettingsProvider and after AuthProvider
 * so that user data is available before feature checking begins
 */
export const FeatureAccessProvider: React.FC<FeatureAccessProviderProps> = ({
  children,
}) => {
  const { user, userProfile } = useAuth();
  const { plans, loading: plansLoading } = usePlans();
  const { features, loading: featuresLoading } = useFeatures();

  // Get user's current plan
  const userPlan = useMemo(() => {
    if (!userProfile?.plan || !user) return null;

    // Get plan name from userProfile
    const planName =
      typeof userProfile.plan === "string"
        ? userProfile.plan
        : userProfile.plan.name;

    // Normalize plan name for robust matching (handles voice transcription variants)
    const normalizedName = planName.trim().toLowerCase();

    // Find matching plan in Firestore
    return plans.find((p) => p.category === normalizedName) || null;
  }, [userProfile?.plan, plans, user]);

  // Get features accessible to user
  const accessibleFeatures = useMemo(() => {
    if (!user || !userPlan) {
      // No plan or not logged in = free tier (basic plan)
      const basicPlan = plans.find((p) => p.category === "basic");
      if (!basicPlan) return [];
      const basicFeatureIds = new Set(basicPlan.features);
      return features.filter((f) => basicFeatureIds.has(f.id));
    }

    const userFeatureIds = new Set(userPlan.features);
    return features.filter((f) => userFeatureIds.has(f.id));
  }, [userPlan, plans, features, user]);

  // Get features NOT accessible to user
  const inaccessibleFeatures = useMemo(() => {
    if (!user) {
      // Not logged in - all features are inaccessible
      return features;
    }

    const accessibleIds = new Set(accessibleFeatures.map((f) => f.id));
    return features.filter((f) => !accessibleIds.has(f.id));
  }, [user, features, accessibleFeatures]);

  // Check if user has access to a feature
  const hasFeature = (featureId: string): boolean => {
    if (!user) return false;
    return accessibleFeatures.some((f) => f.id === featureId);
  };

  const value: FeatureAccessContextType = {
    hasFeature,
    userPlan,
    accessibleFeatures,
    inaccessibleFeatures,
    loading: plansLoading || featuresLoading,
    error: null,
  };

  return (
    <FeatureAccessContext.Provider value={value}>
      {children}
    </FeatureAccessContext.Provider>
  );
};
